import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import {
  Moon, Sun, RefreshCcw, Clock, User as UserIcon,
  ShieldCheck, LogOut, Crown, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";
import { apiGet, apiPost, formatDateTime, formatDateTimeSLT } from "@/lib/api";
import { toast } from "sonner";
import NotificationBell from "@/components/layout/NotificationBell";
import MarketStatusPill from "@/components/common/MarketStatusPill";
import GlossaryDialog from "@/components/common/GlossaryDialog";
import TrialCountdownChip from "@/components/common/TrialCountdownChip";

const PATH_TITLES = {
  "/dashboard": ["Market Overview", "Daily snapshot of CSE-listed companies"],
  "/stocks": ["Stocks", "All securities, voting and non-voting"],
  "/watchlist": ["My Watchlist", "Stocks you're monitoring before buying"],
  "/portfolio": ["My Portfolio", "Setup, AI suggestion and live tracking"],
  "/best-value": ["Best Value Finder", "Non-voting yield gap opportunities"],
  "/dividends": ["Dividends", "Historical dividend records"],
  "/ai-insights": ["AI Insights", "Claude analysis across all companies"],
  "/alerts": ["Alerts", "Custom alerts for prices, signals, dividends and yields"],
  "/reports": ["Reports", "Annual and quarterly company reports"],
  "/system-status": ["System Status", "Refresh logs and scheduler health"],
  "/glossary": ["Glossary", "Plain-English definitions of CSE and financial terms"],
  "/market-intelligence": ["Market Intelligence", "Live market pulse, macro indicators, foreign flows and dividends"],
  "/admin/market-intelligence": ["Market Intelligence Upload", "Upload CSE official and broker research PDFs"],
  "/backtest": ["Strategy Backtesting", "Test how BUY signals would have performed historically"],
};

const TopBar = () => {
  const { theme, toggle } = useTheme();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, isAdmin, logout } = useAuth();
  let key = "/" + (pathname.split("/")[1] || "dashboard");
  // For nested admin paths, prefer the full path key when available
  if (PATH_TITLES[pathname]) key = pathname;
  const [title, subtitle] = PATH_TITLES[key] || ["CSE Investor", ""];

  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadLast = () => {
    apiGet("/scrape-logs?limit=1").then((r) => {
      if (r && r.length) setLastRefresh(r[0].scrape_date);
    }).catch(() => {});
  };

  useEffect(() => {
    loadLast();
    const onRefreshed = () => loadLast();
    window.addEventListener("cse:data-refreshed", onRefreshed);
    const interval = setInterval(loadLast, 60000);
    return () => {
      window.removeEventListener("cse:data-refreshed", onRefreshed);
      clearInterval(interval);
    };
  }, []);

  const onRefresh = async () => {
    try {
      setRefreshing(true);
      await apiPost("/admin/refresh-data");
      toast.success("Market data refreshed");
      loadLast();
      window.dispatchEvent(new Event("cse:data-refreshed"));
    } catch (e) {
      toast.error("Refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <header className="sticky top-0 z-20 border-b border-border bg-card/85 backdrop-blur supports-[backdrop-filter]:bg-card/70">
      <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <h1 className="font-display text-lg md:text-xl font-semibold truncate">{title}</h1>
            <p className="text-xs md:text-sm text-muted-foreground truncate">{subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <TrialCountdownChip />
          <MarketStatusPill />
          <div
            data-testid="topbar-last-refresh"
            className="hidden md:inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs text-muted-foreground"
            title="Last data refresh"
          >
            <Clock className="h-3.5 w-3.5" />
            <span className="font-medium text-foreground">Last refresh</span>
            <span className="tabular">{lastRefresh ? formatDateTimeSLT(lastRefresh) : "—"}</span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={refreshing}
            data-testid="topbar-refresh-button"
            className="gap-2"
          >
            <RefreshCcw className={`h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">{refreshing ? "Refreshing" : "Refresh"}</span>
          </Button>
          <NotificationBell />
          <GlossaryDialog />
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Toggle theme"
            data-testid="topbar-theme-toggle"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>
          {user && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="h-9 gap-2 px-2 sm:px-3"
                  data-testid="topbar-user-menu"
                  aria-label="Account menu"
                >
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-xs font-semibold">
                    {(user.first_name || user.full_name || user.email || "U").charAt(0).toUpperCase()}
                  </span>
                  <span className="hidden md:inline-flex flex-col items-start leading-tight">
                    <span className="text-xs font-medium truncate max-w-[140px]">
                      {user.first_name || user.full_name || user.email}
                    </span>
                    {/* BETA MODE — only admins see a role label below their
                        name. Regular users get just their name (no
                        tier/subscription wording during the free beta). */}
                    {isAdmin && (
                      <span className="text-[10px] text-muted-foreground capitalize">
                        {user.role}
                      </span>
                    )}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64" data-testid="topbar-user-menu-content">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span className="font-medium truncate">{user.full_name || user.first_name || "—"}</span>
                    <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                    {/* BETA MODE — only the admin role badge is shown.
                        Tier/subscription badges are hidden for all users
                        because every authenticated account has full
                        Premium access during the closed beta. */}
                    {isAdmin && (
                      <div className="mt-1.5 flex flex-wrap gap-1">
                        <Badge variant="outline" className="text-[10px] capitalize">
                          <ShieldCheck className="h-3 w-3 mr-1" />
                          {user.role}
                        </Badge>
                      </div>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="topbar-menu-profile">
                  <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                    <UserIcon className="h-4 w-4" /> Profile
                  </Link>
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem asChild data-testid="topbar-menu-admin-users">
                    <Link to="/admin/users" className="flex items-center gap-2 cursor-pointer">
                      <ShieldCheck className="h-4 w-4" /> User Management
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    logout();
                    toast.success("Signed out");
                    navigate("/login", { replace: true });
                  }}
                  className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))] cursor-pointer"
                  data-testid="topbar-menu-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      {/* Mobile last-refresh strip */}
      <div
        data-testid="topbar-last-refresh-mobile"
        className="md:hidden px-4 py-2 border-t border-border bg-background/60 text-xs text-muted-foreground flex items-center gap-1.5"
      >
        <Clock className="h-3 w-3" />
        <span className="font-medium text-foreground">Last refresh:</span>
        <span className="tabular">{lastRefresh ? formatDateTimeSLT(lastRefresh) : "—"}</span>
      </div>
    </header>
  );
};

export default TopBar;
